
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React from 'react';
import { motion, cubicBezier } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import { blogPosts } from '@shared/data/blogData';
import { authors } from '@shared/data/authorsData';
import { ChevronLeftIcon } from '../components/icons';
import { AppPage } from '../types';
import { CDN_URLS } from '@shared/config';
import { getImageUrl } from '../utils/imageUtils';
import Footer from '../components/Footer';
import SchemaMarkup from '../components/SchemaMarkup';

interface BlogPostPageProps {
  postId: string;
  onNavigate: (page: AppPage, params?: Record<string, string>) => void;
}

const BlogPostPage: React.FC<BlogPostPageProps> = ({ postId, onNavigate }) => {
  const { t } = useTranslation();

  const post = blogPosts.find((p: any) => p.id === postId);
  const author = post ? authors.find((a: any) => a.id === post.authorId) : null;
  const relatedPosts = post ? blogPosts.filter((p: any) => p.id !== postId).slice(0, 2) : [];


  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: cubicBezier(0.42, 0, 0.58, 1) } },
  };


  if (!post) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-4 sm:p-8 bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200">
        <h1 className="text-2xl font-serif">{t('blog_post_not_found')}</h1>
        <button onClick={() => onNavigate('blog')} className="mt-4 text-indigo-600 dark:text-indigo-400 font-semibold hover:underline">
          &larr; {t('blogPost_back')}
        </button>
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={sectionVariants}
      className="h-full flex flex-col items-center p-4 sm:p-8 bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 overflow-y-auto custom-scrollbar"
    >
      <div className="max-w-6xl w-full mb-12">
        <div className="mb-8">
          <button onClick={() => onNavigate('blog')} className="flex items-center text-indigo-600 dark:text-indigo-400 font-semibold hover:underline">
            <ChevronLeftIcon className="w-5 h-5 mr-1" />
            {t('blogPost_back')}
          </button>
        </div>
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* Main Content */}
          <div className="w-full lg:w-2/3">
            <article className="prose prose-lg dark:prose-invert max-w-none">
              <header>
                <h1 className="font-serif !text-4xl md:!text-5xl !text-gray-900 dark:!text-white !leading-tight !mb-4">
                  {t(post.titleKey)}
                </h1>
                <p className="text-gray-500 dark:text-gray-400">{t(post.dateKey || '')}</p>
              </header>

              <img
                src={getImageUrl(post.imageUrl)}
                alt={t(post.titleKey)}
                className="w-full aspect-video object-cover rounded-lg my-8 shadow-md"
              />

              <p className="lead !text-xl !text-gray-600 dark:!text-gray-400">
                {t(post.excerptKey)}
              </p>

              <div dangerouslySetInnerHTML={{ __html: t(post.contentKey || '') }} />
            </article>
          </div>
          {/* Sidebar */}
          <aside className="w-full lg:w-1/3">
            <div className="sticky top-24 space-y-8">
              {author && (
                <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-xl font-serif font-semibold text-gray-900 dark:text-white mb-4">{t('blogPost_author_title')}</h3>
                  <div className="flex items-center gap-4">
                    <img src={getImageUrl(author.avatarUrl)} alt={t(author.nameKey)} className="w-16 h-16 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700" />
                    <div>
                      <h4 className="font-semibold text-lg text-gray-800 dark:text-gray-200">
                        <button onClick={() => onNavigate('author', { authorId: author.id })} className="hover:underline">{t(author.nameKey)}</button>
                      </h4>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">{t(author.bioKey)}</p>
                    </div>
                  </div>
                </div>
              )}
              {relatedPosts.length > 0 && (
                <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-xl font-serif font-semibold text-gray-900 dark:text-white mb-4">{t('blogPost_related_title')}</h3>
                  <div className="space-y-4">
                    {relatedPosts.map((related: any) => (
                      <button key={related.id} onClick={() => onNavigate('blog-post', { postId: related.id })} className="group flex items-center gap-4 text-left p-2 -m-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors">
                        <img src={getImageUrl(related.imageUrl)} alt={t(related.titleKey)} className="w-24 h-24 object-cover rounded-md flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-gray-800 dark:text-gray-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">{t(related.titleKey)}</h4>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
      <SchemaMarkup
        type="Article"
        data={{
          headline: t(post.titleKey),
          description: t(post.excerptKey),
          image: getImageUrl(post.imageUrl),
          datePublished: post.date,
          dateModified: post.dateModified || post.date,
          author: {
            "@type": "Person",
            name: author ? t(author.nameKey) : "Constantina Team",
            description: author ? t(author.bioKey) : undefined
          },
          publisher: {
            "@type": "Organization",
            name: "Constantina",
            logo: {
              "@type": "ImageObject",
              url: `${CDN_URLS.LOGO}`
            }
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `${window.location.origin}/blog-post/${post.id}`
          }
        }}
      />
      <Footer onNavigate={onNavigate} />
    </motion.div>
  );
};

export default BlogPostPage;
